"use client";

import type { ShowArtist } from "@/lib/types/show-types";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Label } from "@workspace/ui/components/label";
import { Textarea } from "@workspace/ui/components/textarea";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReviewLovedButton } from "@/components/features/festival/review/review-loved-button";
import { ReviewStars } from "@/components/features/festival/review/review-stars";
import { ReviewStatusButton } from "@/components/features/festival/review/review-status-button";
import { GenreBadgeList } from "@/components/shared/genre-badge-list";
import { ClickableArtistPhoto } from "@/components/shared/photos/clickable-artist-photo";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { formatTimeRangeWithPreferences } from "@workspace/shared-utils";
import { useRealtimeSupportingSetReview } from "@/hooks/features/show/use-realtime-supporting-set-review";
import { useRealtimeShowReview } from "@/hooks/features/show/use-realtime-show-review";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { isEmptyReview } from "@workspace/shared-utils";
import { useCurrentUser } from "@/stores";
import { parseGenres } from "@workspace/types";

interface ShowSetReviewModalProps {
	isOpen: boolean;
	onClose: () => void;
	showArtist: ShowArtist | null;
	showId: string;
	showSlug: string;
	showTitle: string;
	showDate: string;
	venue?: string;
	location?: string;
	onSaved?: () => void;
}

export function ShowSetReviewModal({
	isOpen,
	onClose,
	showArtist,
	showId,
	showSlug,
	showTitle,
	onSaved,
}: ShowSetReviewModalProps) {
	const currentUser = useCurrentUser();
	const userPreferences = useUserPreferences();

	// Local state for form inputs
	const [localRating, setLocalRating] = useState(0);
	const [localContent, setLocalContent] = useState("");
	const [localLoved, setLocalLoved] = useState(false);
	const [localSawLive, setLocalSawLive] = useState(false);
	const [localSawStream, setLocalSawStream] = useState(false);
	const [localWantToSee, setLocalWantToSee] = useState(false);
	const [localMustSee, setLocalMustSee] = useState(false);

	const isHeadliner = showArtist?.role === "headliner";
	const artistName = showArtist?.artist?.name || "Unknown Artist";
	const artistId = showArtist?.artist?.id || showArtist?.artist_id || "";
	const artistImageUrl = showArtist?.artist?.image_url;

	const genreArray = parseGenres(
		showArtist?.artist?.spotify_genres as string[] | null || null,
		showArtist?.artist?.manual_genres as string[] | null || null
	);
	const hasGenres = genreArray.length > 0;

	const { review: supportingReview, saveReview: saveSupportingReview, isPending: isSupportingPending } = useRealtimeSupportingSetReview({
		showId,
		showArtistId: showArtist?.id || "",
		artistId,
		userId: currentUser?.id || null,
		enabled: !!currentUser?.id && !isHeadliner && !!showArtist?.id && isOpen,
	});

	const { review: showReview, saveReview: saveShowReview, isPending: isShowPending } = useRealtimeShowReview({
		showId,
		artistId,
		userId: currentUser?.id || null,
		enabled: !!currentUser?.id && isHeadliner && isOpen,
	});

	const review = isHeadliner ? showReview : supportingReview;
	const saveReview = isHeadliner ? saveShowReview : saveSupportingReview;
	const isPending = isHeadliner ? isShowPending : isSupportingPending;

	// Track if we had a review when the modal opened (for button text logic)
	const hadInitialReviewRef = useRef(false);

	useEffect(() => {
		if (isOpen) {
			hadInitialReviewRef.current = !!review;
		}
	}, [isOpen, review]);

	// Initialize form with existing review data
	useEffect(() => {
		if (isOpen && review) {
			setLocalRating(review.rating || 0);
			setLocalContent(review.content || "");
			setLocalLoved(review.loved || false);
			setLocalSawLive(review.sawLive || false);
			setLocalSawStream(review.sawStream || false);
			setLocalWantToSee(review.wantToSee || false);
			setLocalMustSee(review.mustSee || false);
		} else if (isOpen && !review) {
			// Reset to defaults if no review exists
			setLocalRating(0);
			setLocalContent("");
			setLocalLoved(false);
			setLocalSawLive(false);
			setLocalSawStream(false);
			setLocalWantToSee(false);
			setLocalMustSee(false);
		}
	}, [isOpen, review]);

	const handleSave = useCallback(async () => {
		if (!currentUser) return;

		await saveReview({
			rating: localRating,
			content: localContent,
			loved: localLoved,
			sawLive: localSawLive,
			sawStream: localSawStream,
			wantToSee: localWantToSee,
			mustSee: localMustSee,
		});

		onClose();
		onSaved?.();
	}, [
		currentUser,
		localRating,
		localContent,
		localLoved,
		localSawLive,
		localSawStream,
		localWantToSee,
		localMustSee,
		saveReview,
		onClose,
		onSaved,
	]);

	// Handle status button changes with proper exclusivity
	const handleStatusChange = useCallback(
		(status: string) => {
			if (status === "sawLive") {
				setLocalSawLive(!localSawLive);
				setLocalSawStream(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			} else if (status === "sawStream") {
				setLocalSawStream(!localSawStream);
				setLocalSawLive(false);
				setLocalWantToSee(false);
				setLocalMustSee(false);
			} else if (status === "wantToSee") {
				setLocalWantToSee(!localWantToSee);
				setLocalMustSee(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
			} else if (status === "mustSee") {
				setLocalMustSee(!localMustSee);
				setLocalWantToSee(false);
				setLocalSawLive(false);
				setLocalSawStream(false);
			}
		},
		[localSawLive, localSawStream, localWantToSee, localMustSee]
	);

	// Create a review data object from local state to check if it's empty
	const localReviewData = {
		rating: localRating,
		content: localContent,
		loved: localLoved,
		sawLive: localSawLive,
		sawStream: localSawStream,
		wantToSee: localWantToSee,
		mustSee: localMustSee,
	};

	const isLocalReviewEmpty = isEmptyReview(localReviewData);

	const timeDisplay = showArtist?.time_start && showArtist?.time_end
		? formatTimeRangeWithPreferences(
				showArtist.time_start,
				showArtist.time_end,
				userPreferences
			)
		: null;

	if (!showArtist) return null;

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="w-full max-w-[95%] p-0 sm:max-w-md">
				<DialogTitle className="sr-only">Review Set</DialogTitle>
				<DialogDescription className="sr-only">
					Rate and review this set. Share your experience and add personal notes
					about the performance.
				</DialogDescription>
				<DialogHeader className="space-y-4 px-6 pt-6 pb-0">
					<div className="flex gap-3">
						<ClickableArtistPhoto
							href={`/artists/${createArtistSlug(artistName, showArtist?.artist?.slug)}`}
							name={artistName}
							imageUrl={artistImageUrl}
							size="lg"
							rounded="card"
							trackingData={{
								artistId,
								artistName,
								source: "discover",
							}}
						/>
						<div className="flex flex-1 flex-col gap-1">
							<h3 className="font-semibold text-base leading-tight">
								{artistName}
								{isHeadliner && (
									<span className="ml-1 text-sm text-muted-foreground">(Headliner)</span>
								)}
							</h3>
							<div className="text-muted-foreground text-xs">
								{timeDisplay && <div>{timeDisplay}</div>}
								<div>{showTitle}</div>
							</div>
						</div>
					</div>

					{hasGenres && (
						<div className="space-y-1">
							<Label>Genres</Label>
							<GenreBadgeList genres={genreArray} className="gap-2" />
						</div>
					)}
				</DialogHeader>

				<div className="px-6">
					<div className="space-y-4">
						<div className="relative flex items-center justify-center">
							<div className="flex items-center">
								<ReviewStars rating={localRating} onChange={setLocalRating} />

								<div className="mx-2 h-10 w-px bg-border/50 md:mx-4 md:h-14" />

								<ReviewLovedButton
									loved={localLoved}
									onChange={setLocalLoved}
								/>
							</div>
						</div>

						<div className="flex flex-wrap justify-center gap-1">
							<ReviewStatusButton
								icon="eye"
								label="Want to See"
								isActive={localWantToSee}
								activeClassName="text-radix-plum-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("wantToSee")}
							/>
							<ReviewStatusButton
								icon="flag"
								label="Must See"
								isActive={localMustSee}
								activeClassName="text-radix-violet-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("mustSee")}
							/>
							<ReviewStatusButton
								icon="mic"
								label="Saw Live"
								isActive={localSawLive}
								activeClassName="text-radix-indigo-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("sawLive")}
							/>
							<ReviewStatusButton
								icon="live"
								label="Saw Stream"
								isActive={localSawStream}
								activeClassName="text-radix-ruby-9"
								inactiveClassName="hover:bg-muted text-muted-foreground"
								onClick={() => handleStatusChange("sawStream")}
							/>
						</div>

						<Textarea
							placeholder="Write your review here (optional)..."
							className="min-h-[100px]"
							value={localContent}
							onChange={(e) => setLocalContent(e.target.value)}
						/>
					</div>
				</div>

				<DialogFooter className="px-6 pt-0 pb-6">
					<div className="flex w-full items-center justify-between">
						<Button variant="secondary" asChild>
							<Link href={`/shows/${showSlug}/${showArtist.id}`}>
								View Details
							</Link>
						</Button>

						<div className="flex gap-2">
							<Button
								variant="ghost"
								onClick={onClose}
								disabled={isPending}
								type="button"
							>
								Cancel
							</Button>
							<Button
								onClick={handleSave}
								disabled={isPending}
								variant={
									isLocalReviewEmpty && hadInitialReviewRef.current
										? "destructive"
										: "default"
								}
								type="button"
							>
								{isPending
									? "Saving..."
									: isLocalReviewEmpty && hadInitialReviewRef.current
										? "Remove Review"
										: hadInitialReviewRef.current
											? "Update"
											: "Save"}
							</Button>
						</div>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}